import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Print as PrintIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { CommonService } from "../../services/CommonService";

interface PriceUpdate {
  id: number;
  created_at: string;
  created_by: {
    id: number;
    name: string;
  };
  products_count: number;
  summary: {
    total_products: number;
    price_increases: number;
    price_decreases: number;
  };
  status: number;
}

interface PriceUpdatesModalProps {
  open: boolean;
  onClose: () => void;
  onViewDetails: (updateId: number) => void;
}

export const PriceUpdatesModal: React.FC<PriceUpdatesModalProps> = ({
  open,
  onClose,
  onViewDetails,
}) => {
  const [updates, setUpdates] = useState<PriceUpdate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Reset state when opening
      setUpdates([]);
      setError(null);
      loadRecentUpdates();
    }
  }, [open]);

  const loadRecentUpdates = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await CommonService.getRecentPriceUpdates(20, 7);
      console.log('🔍 DEBUGGING: Price updates response:', response);

      if (response && response.success) {
        // Handle different response structures
        let updatesData = [];
        if (response.object) {
          updatesData = Array.isArray(response.object) ? response.object : [];
        } else if (response.data) {
          updatesData = Array.isArray(response.data) ? response.data : [];
        }

        console.log('🔍 DEBUGGING: Updates data processed:', updatesData);
        updatesData.forEach((update, index) => {
          console.log(`🔍 Update ${index + 1}:`, {
            id: update.id,
            summary: update.summary,
            increases: update.summary?.price_increases,
            decreases: update.summary?.price_decreases
          });
        });

        // TODO: Temporary fix - swap increases/decreases if they seem incorrect
        // This should be fixed in the backend
        const correctedUpdates = updatesData.map(update => {
          if (update.summary) {
            console.log(`🔧 TEMP FIX: Swapping increases/decreases for update ${update.id}`);
            return {
              ...update,
              summary: {
                ...update.summary,
                price_increases: update.summary.price_decreases,
                price_decreases: update.summary.price_increases
              }
            };
          }
          return update;
        });

        setUpdates(correctedUpdates);
      } else {
        console.error('Invalid response structure:', response);
        // For now, show a message that the feature is not available
        setError('La funcionalidad de actualizaciones de precios no está disponible en este momento');
        setUpdates([]);
      }
    } catch (err: any) {
      console.error('Error loading price updates:', err);
      // Check if it's a 404 or network error
      if (err.message?.includes('404') || err.status === 404) {
        setError('La funcionalidad de actualizaciones de precios no está disponible en este momento');
      } else {
        setError('Error de conexión al cargar las actualizaciones');
      }
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1:
        return 'warning'; // Pending
      case 2:
        return 'success'; // Processed
      default:
        return 'default';
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 1:
        return 'Pendiente';
      case 2:
        return 'Procesado';
      default:
        return 'Desconocido';
    }
  };

  const handleViewDetails = (updateId: number) => {
    onViewDetails(updateId);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Actualizaciones de Precios Recientes</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && updates && updates.length === 0 && (
          <Box textAlign="center" p={3}>
            <Typography color="textSecondary">
              No hay actualizaciones de precios recientes
            </Typography>
          </Box>
        )}

        {!loading && !error && updates && updates.length > 0 && (
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Mostrando las últimas {updates?.length || 0} actualizaciones de los últimos 7 días
            </Typography>

            <Grid container spacing={2}>
              {updates?.map((update) => (
                <Grid item xs={12} key={update.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box>
                          <Typography variant="h6" component="div">
                            Actualización #{update.id}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1} mt={1}>
                            <ScheduleIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="textSecondary">
                              {formatDate(update.created_at)}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="textSecondary">
                              {update.created_by.name}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          label={getStatusText(update.status)}
                          color={getStatusColor(update.status)}
                          size="small"
                        />
                      </Box>

                      <Box display="flex" gap={2} mb={2}>
                        <Chip
                          icon={<PrintIcon />}
                          label={`${update.products_count} productos`}
                          variant="outlined"
                          size="small"
                        />
                        {update.summary.price_increases > 0 && (
                          <Chip
                            icon={<TrendingUpIcon />}
                            label={`${update.summary.price_increases} aumentos`}
                            color="success"
                            variant="outlined"
                            size="small"
                          />
                        )}
                        {update.summary.price_decreases > 0 && (
                          <Chip
                            icon={<TrendingDownIcon />}
                            label={`${update.summary.price_decreases} reducciones`}
                            color="error"
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </Box>


                    </CardContent>

                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewDetails(update.id)}
                        variant="outlined"
                      >
                        Ver Detalles
                      </Button>
                      <Button
                        size="small"
                        startIcon={<PrintIcon />}
                        onClick={() => handleViewDetails(update.id)}
                        variant="contained"
                        color="primary"
                        sx={{ ml: 1 }}
                      >
                        Imprimir Etiquetas
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              )) || []}
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button onClick={loadRecentUpdates} variant="outlined">
          Actualizar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
